/**
 * Unpublish dialog (Phase 1 user decision: admin "force-cancel everything"
 * is in scope).
 *
 *   ( ) Soft-retire (in-flight finishes)        default
 *   ( ) Force-cancel all in-flight              admin only — warning
 *
 * Calls onSubmit({ forceCancel, reason }). The route layer enforces admin
 * for force-cancel; we mirror the gate in the UI.
 */

import { useState } from 'react';
import { Button } from '../../ui/Button.js';
import { Textarea } from '../../ui/Input.js';

export interface UnpublishChoice {
  forceCancel: boolean;
  reason?: string;
}

export function UnpublishDialog({
  open,
  isAdmin,
  activeInstanceCount,
  isPending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  isAdmin: boolean;
  activeInstanceCount: number;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (choice: UnpublishChoice) => void;
}) {
  const [mode, setMode] = useState<'soft' | 'force'>('soft');
  const [reason, setReason] = useState('');

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-floating"
      >
        <h3 className="font-display text-lg font-bold text-brand-navy-800">
          Unpublish workflow
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          {activeInstanceCount} active loan{activeInstanceCount === 1 ? '' : 's'} currently running this workflow.
        </p>

        <fieldset className="mt-4 space-y-3 text-sm">
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-brand-navy-50">
            <input
              type="radio"
              checked={mode === 'soft'}
              onChange={() => setMode('soft')}
              className="mt-1 h-4 w-4 border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
            />
            <div>
              <div className="font-medium text-brand-navy-800">Soft-retire</div>
              <div className="text-xs text-slate-500">
                Stop new starts. In-flight loans finish normally. Reversible.
              </div>
            </div>
          </label>

          <label
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
              isAdmin
                ? 'border-slate-200 hover:bg-rose-50'
                : 'cursor-not-allowed border-slate-100 bg-slate-50 opacity-60'
            }`}
          >
            <input
              type="radio"
              checked={mode === 'force'}
              onChange={() => isAdmin && setMode('force')}
              disabled={!isAdmin}
              className="mt-1 h-4 w-4 border-slate-300 text-rose-600 focus:ring-rose-300"
            />
            <div>
              <div className="flex items-center gap-2 font-medium text-rose-700">
                Force-cancel all in-flight
                {!isAdmin && (
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                    admin only
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">
                Every in-flight instance is cancelled with a per-instance audit row. This cannot be undone.
              </div>
            </div>
          </label>
        </fieldset>

        <label className="mt-3 block">
          <span className="field-label">Reason (optional)</span>
          <Textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Logged in the audit trail."
          />
        </label>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={mode === 'force' ? 'danger' : 'primary'}
            size="sm"
            disabled={isPending}
            onClick={() => onSubmit({ forceCancel: mode === 'force', reason: reason.trim() || undefined })}
          >
            {isPending
              ? 'Unpublishing…'
              : mode === 'force'
                ? 'Force-cancel & unpublish'
                : 'Unpublish'}
          </Button>
        </div>
      </div>
    </div>
  );
}
